/**
 * WordPress dependencies
 */
import { store, getContext, getElement } from '@wordpress/interactivity';

const formatDate = (dateStr) => {
    const dateParts = dateStr.split('/');
    if (dateParts.length === 3) {
        // Assuming format is DD/MM/YYYY
        return new Date(`${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`);
    } else {
        // Assuming ISO format YYYY-MM-DD
        return new Date(dateStr);
    }
};


const getRequestedDates = () => {
    const rawArrival = Cookies.get('gtArrival');
    const rawDeparture = Cookies.get('gtDeparture');

    if (!rawArrival || !rawDeparture) {
        return {
            arrival: null,
            departure: null,
        };
    }

    return {
        arrival: formatDate(rawArrival),
        departure: formatDate(rawDeparture),
    };
};

const enrichStayStrikePrice = (stay) => {
    if (!stay) {
        return;
    }
    const original = Number(stay.gtPriceOriginal);
    const discounted = Number(stay.gtPriceDiscounted);
    stay.gtShowStrikePrice = !!(stay.productSpecialOffer && original !== discounted);
};

const splitStaysByDates = (staysCollection = [], requestedArrival, requestedDeparture, limitAlternates = false) => {
    if (!requestedArrival || !requestedDeparture || !Array.isArray(staysCollection)) {
        return {
            exact: [],
            alternates: [],
        };
    }

    const exact = [];
    const alternates = [];

    staysCollection.forEach((stay) => {
        if (!stay?.productAvailabilityDate) {
            return;
        }

        if (stay.productSpecialOffer && !stay.gtPromoLabel) {
            stay.gtPromoLabel = stay.productSpecialOffer.OS_shortDescription
                || stay.productSpecialOffer.OS_genericDescription
                || '';
        }

        enrichStayStrikePrice(stay);

        const dateBegin = formatDate(stay.productAvailabilityDate.dateBegin.split('T')[0]);
        const dateEnd = formatDate(stay.productAvailabilityDate.dateEnd.split('T')[0]);

        if (dateBegin.getTime() === requestedArrival.getTime() && dateEnd.getTime() === requestedDeparture.getTime()) {
            exact.push(stay);
        } else {
            alternates.push(stay);
        }
    });

    exact.sort((a, b) => a.gtPriceDiscounted - b.gtPriceDiscounted);
    alternates.sort((a, b) => a.gtPriceDiscounted - b.gtPriceDiscounted);

    if (limitAlternates && alternates.length > 5) {
        return {
            exact,
            alternates: alternates.slice(0, 5),
        };
    }

    return { exact, alternates };
};

const { state } = store("gt/ctv", {
    state: {
        get currentDisponibilites(){
            let context;
            try { context = getContext(); } catch(e) { return []; }
            if (!context) {
                return [];
            }
            const { idProduct, campingKey, isCamping } = context;
            const { stays } = state;

            const { arrival: Arrival, departure: Departure } = getRequestedDates();

            context.datesTitles = "";

            if(!Arrival || !Departure){
                return [];
            }

            if(isCamping){
                if (!stays || Object.keys(stays).length === 0) {
                    return [];
                }

                let campingStays = [];
                for (const stay of Object.values(stays)) {
                    
                    if (stay.length === 0) {
                        continue;
                    }

                    if(stay[0].campingkey == campingKey){
                        campingStays.push(...stay);
                    }
                }

                return splitStaysByDates(campingStays, Arrival, Departure).exact;
            }
            
            // Herbegement

            if(!idProduct) {
                return [];
            }

            if (!stays || !(idProduct in stays) || stays[idProduct].length === 0) {
                return [];
            }
            
            const { exact } = splitStaysByDates(stays[idProduct], Arrival, Departure, true);
            return exact;
        },
        get currentContrepropositions(){
            let context;
            try { context = getContext(); } catch(e) { return []; }
            if (!context) {
                return [];
            }
            const { idProduct, campingKey, isCamping } = context;
            const { stays } = state;
            const { arrival: Arrival, departure: Departure } = getRequestedDates();

            if(!Arrival || !Departure){
                return [];
            }

            if(isCamping){
                if (!stays || Object.keys(stays).length === 0) {
                    return [];
                }

                let campingStays = [];
                for (const stay of Object.values(stays)) {
                    if (stay.length === 0) {
                        continue;
                    }

                    if(stay[0].campingkey == campingKey){
                        campingStays.push(...stay);
                    }
                }

                return splitStaysByDates(campingStays, Arrival, Departure).alternates;
            }

            if(!idProduct || !stays || !(idProduct in stays) || stays[idProduct].length === 0){
                return [];
            }

            const { alternates } = splitStaysByDates(stays[idProduct], Arrival, Departure, true);
            return alternates;
        },
        get mainStay(){
            const allStays = [...state.currentDisponibilites, ...state.currentContrepropositions];
            let context;
            try { context = getContext(); } catch(e) { return allStays[0] ?? null; }
            
            if (!context) {
                return allStays[0] ?? null;
            }

            context.mainStay = allStays[0];

            return allStays[0];
        },
        get statut (){
            const {mainStay, currentDisponibilites, currentContrepropositions } = state;
            const { arrival: Arrival, departure: Departure } = getRequestedDates();

            if(!mainStay || !Arrival || !Departure) return 'indisponible';
            if(currentDisponibilites.length > 0){
                const dateBegin = formatDate(mainStay.productAvailabilityDate.dateBegin.split('T')[0]);
                const dateEnd = formatDate(mainStay.productAvailabilityDate.dateEnd.split('T')[0]);
                if(dateBegin.getTime() === Arrival.getTime() && dateEnd.getTime() === Departure.getTime()){
                    return 'disponible';
                }
            }

            if(currentContrepropositions.length > 0){
                return 'contreproposition';
            }
            return 'indisponible';

        },
        get titre(){
            const {statut} = state;
            if(statut === 'disponible') return state.titres.disponible;
            if(statut === 'contreproposition') return state.titres.contreproposition;
            return state.titres.indisponible;
        },
        get gtPriceDiscounted(){
            const { mainStay } = state;
            if(!mainStay) return 0;
            const { gtPriceDiscounted } = mainStay;
            return gtPriceDiscounted;
        },
        get gtPriceOriginal(){
            const { mainStay } = state;
            if(!mainStay) return 0;
            const { gtPriceOriginal } = mainStay;
            return gtPriceOriginal;
        },
        get gtPromoName(){
            const { mainStay } = state;
            if(!mainStay || !mainStay.productSpecialOffer) return '';
            return mainStay.productSpecialOffer.OS_shortDescription || mainStay.productSpecialOffer.OS_genericDescription || '';
        },
        get hasPromo(){
            return !!(state.mainStay && state.mainStay.productSpecialOffer);
        },
        get showStrikePrice(){
            const { mainStay } = state;
            return !!(mainStay && mainStay.gtShowStrikePrice);
        },
        /** Pour le bloc Disponibilités V2 : n'afficher dispos/contre-propositions que si des dates sont dans l'URL */
        get showDisponibilitesV2(){
            try {
                const url = new URL(window.location.href);
                const a = url.searchParams.get('arrival');
                const d = url.searchParams.get('departure');
                return !!(a && d);
            } catch (e) {
                return false;
            }
        },
        get txtFinalDates(){
            const { mainStay } = state;
            if(!mainStay) return '';
            return mainStay.productAvailabilityDate.txtFinalDates
        },
        get nextDispo(){

            if(state.statut != "indisponible") return;
            const context = getContext();
            if (!context) {
                return;
            }
        
            // Fonction pour parser les dates (format DD/MM/YYYY)
            const parseDate = (dateString) => {
                const [day, month, year] = dateString.split('/').map(Number);
                return new Date(year, month - 1, day); // Mois 0-indexé
            };
        
            // Fonction pour formater les dates
            const formatFullDate = (date) => {
                const formatter = new Intl.DateTimeFormat('fr-FR', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                });
                return formatter.format(date);
            };
        
            // Dates demandées
            const rawArrival = Cookies.get('gtArrival');
            const rawDeparture = Cookies.get('gtDeparture');
        
            const requestedArrival = parseDate(rawArrival);
            const requestedDeparture = parseDate(rawDeparture);
        
            if (isNaN(requestedArrival) || isNaN(requestedDeparture)) {
                context.isIndisponible = true;
                return;
            }
        
            const requestedDuration = (requestedDeparture - requestedArrival) / (1000 * 60 * 60 * 24); // Durée demandée en jours
        

            // Récupérer les plannings
            const planningData = state.planings;


            if(!planningData) return;
            let specificProductPlanning = null;
            for (const planning of planningData) {
                const productPlanning = planning?.GetProductsPlanningResult?.ProductsPlanning?.ProductPlanning || [];
                specificProductPlanning = productPlanning.find(product => product.ProductID == parseInt(context.idProduct));
                if (specificProductPlanning) break;
            }

            

            if (!specificProductPlanning) {
                context.isIndisponible = true;
                return;
            }

            if(!specificProductPlanning.AvailabilityPeriods) return;
        
            const availabilityPeriods = Array.isArray(specificProductPlanning.AvailabilityPeriods.AvailabilityPeriod)
                ? specificProductPlanning.AvailabilityPeriods.AvailabilityPeriod
                : [specificProductPlanning.AvailabilityPeriods.AvailabilityPeriod];

        
            // Construire toutes les disponibilités
            const allAvailabilities = availabilityPeriods.map(period => {
                const beginDate = new Date(period.BeginningDate);
                const endDate = new Date(period.EndingDate);
        
                if (isNaN(beginDate) || isNaN(endDate)) {
                    return null;
                }
        
                const periodDuration = (endDate - beginDate) / (1000 * 60 * 60 * 24); // Durée en jours
                const dateDiff = Math.abs(beginDate - requestedArrival); // Distance en millisecondes
        
                return {
                    beginDate,
                    endDate,
                    periodDuration,
                    dateDiff,
                };
            }).filter(av => av !== null); // Exclure les périodes invalides

        
            // Trier par proximité de la date demandée
            const sortedAvailabilities = allAvailabilities.sort((a, b) => a.dateDiff - b.dateDiff);
        
            // Sélectionner la meilleure disponibilité
            let closestAvailability = null;
        
            for (const availability of sortedAvailabilities) {
                const potentialEndDate = new Date(availability.beginDate);
                potentialEndDate.setDate(potentialEndDate.getDate() + requestedDuration);
        
                // Vérifier si la durée demandée peut être respectée dans cette période
                if (potentialEndDate <= availability.endDate) {
                    closestAvailability = {
                        beginDate: availability.beginDate,
                        endDate: potentialEndDate,
                    };
                    break;
                }
            }
        
            if (!closestAvailability && sortedAvailabilities.length > 0) {
                // Si aucune période exacte trouvée, prendre la première disponible
                const firstAvailable = sortedAvailabilities[0];
                closestAvailability = {
                    beginDate: firstAvailable.beginDate,
                    endDate: firstAvailable.endDate, // Durée demandée non respectée
                };
            }
            context.datesTitles = state.titres.prochainesDisponibilites;
            return state.titres.du + ` ${formatFullDate(closestAvailability.beginDate)} ${state.titres.au} ${formatFullDate(closestAvailability.endDate)}`;
            
        },
        get isDisponible(){
            return state.statut === 'disponible';
        },
        get isContreproposition(){
            return state.statut === 'contreproposition';
        },
        get isIndisponible(){
            return state.statut === 'indisponible';
        },
        get hasDisponibilites(){
            return state.currentDisponibilites && state.currentDisponibilites.length > 0;
        },
        get hasContrepropositions(){
            return state.currentContrepropositions && state.currentContrepropositions.length > 0;
        },
        get showContrepropositions(){
            return state.hasContrepropositions && state.statut !== 'disponible';
        },
        get showPrice(){
            return state.statut !== 'indisponible' && state.mainStay && state.mainStay.productPrice;
        },
    },
    actions: {
        /**
         * Charge les disponibilités pour les dates disponibles (URL, cookies, ou state par défaut).
         * Utilisé pour charger automatiquement les disponibilités au chargement de la page.
         */
        loadDisponibilitesFromDates: async () => {
            // Éviter les appels multiples
            if (state._loadingDisponibilites) {
                return;
            }
            state._loadingDisponibilites = true;
            
            const ctvStore = store('gt/ctv');
            
            // 1. Vérifier les paramètres URL
            const urlParams = new URLSearchParams(window.location.search);
            let arrival = urlParams.get('arrival');
            let departure = urlParams.get('departure');
            
            // 2. Sinon vérifier les cookies
            if (!arrival || !departure) {
                arrival = Cookies.get('gtArrival');
                departure = Cookies.get('gtDeparture');
            }
            
            // 3. Sinon utiliser les valeurs par défaut du state
            if ((!arrival || !departure || arrival === 'null' || departure === 'null') && ctvStore.state.gtArrival && ctvStore.state.gtDeparture) {
                arrival = ctvStore.state.gtArrival;
                departure = ctvStore.state.gtDeparture;
            }
            
            // Si aucune date valide trouvée, ne rien faire
            if (!arrival || !departure || arrival === 'null' || departure === 'null') {
                state._loadingDisponibilites = false;
                return;
            }
            
            // Vérifier que les dates sont valides (format DD/MM/YYYY)
            const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
            if (!dateRegex.test(arrival) || !dateRegex.test(departure)) {
                state._loadingDisponibilites = false;
                return;
            }
            
            const baseUrl = state.baseUrl || (window.location.origin + '/wp-content/plugins/gt-ws-ctv/ws/');
            const nbPers = urlParams.get('nbPers') || Cookies.get('gtNbPers') || ctvStore.state.gtNbPers || '2';
            const language = state.language || (document.documentElement.lang ? document.documentElement.lang.slice(0, 2) : 'fr');
            const siteId = state.siteId || 1;
            
            try {
                const response = await fetch(
                    `${baseUrl}disponibilites/?arrival=${encodeURIComponent(arrival)}&departure=${encodeURIComponent(departure)}&nbPers=${encodeURIComponent(nbPers)}&lang=${language}&site_id=${siteId}`
                );
                const disponibilitesData = await response.json();
                if (disponibilitesData && typeof disponibilitesData === 'object') {
                    state.stays = disponibilitesData;
                    // Mettre à jour les cookies pour la prochaine fois
                    Cookies.set('gtArrival', arrival);
                    Cookies.set('gtDeparture', departure);
                    Cookies.set('gtNbPers', nbPers);
                    if (window.dispatchEvent) {
                        window.dispatchEvent(new CustomEvent('gt_ws_ctv_updated'));
                    }
                }
            } catch (error) {
                console.error('GT WS CTV: Erreur chargement disponibilités', error);
            } finally {
                state._loadingDisponibilites = false;
            }
        },
        /**
         * Sur la fiche hébergement (single), refait un fetch des disponibilités pour les dates
         * des cookies afin de resynchroniser state.stays après une navigation client-side.
         */
        refetchStaysForSingleHebergement: async () => {
            const isSingleHebergement = document.querySelector('.single-hebergement');
            if (!isSingleHebergement) {
                return;
            }
            const arrival = Cookies.get('gtArrival');
            const departure = Cookies.get('gtDeparture');
            if (!arrival || !departure) {
                return;
            }
            const baseUrl = state.baseUrl || (window.location.origin + '/wp-content/plugins/gt-ws-ctv/ws/');
            const nbPers = Cookies.get('gtNbPers') || state.gtNbPers || '2';
            const language = state.language || (document.documentElement.lang ? document.documentElement.lang.slice(0, 2) : 'fr');
            const siteId = state.siteId || 1;
            try {
                const response = await fetch(
                    `${baseUrl}disponibilites/?arrival=${encodeURIComponent(arrival)}&departure=${encodeURIComponent(departure)}&nbPers=${encodeURIComponent(nbPers)}&lang=${language}&site_id=${siteId}`
                );
                const disponibilitesData = await response.json();
                if (disponibilitesData && typeof disponibilitesData === 'object') {
                    state.stays = disponibilitesData;
                    if (window.dispatchEvent) {
                        window.dispatchEvent(new CustomEvent('gt_ws_ctv_updated'));
                    }
                }
            } catch (error) {
                console.error('GT WS CTV: Erreur refetch disponibilités fiche hébergement', error);
            }
        },
        /**
         * Init du bloc : charge les disponibilités si des dates sont disponibles,
         * puis refetch sur fiche hébergement (navigation client-side),
         * puis sélection du mainStay (radio).
         */
        initDisponibilites: async () => {
            const ctvStore = store('gt/ctv');
            // Charger les disponibilités automatiquement si des dates sont disponibles
            await ctvStore.actions.loadDisponibilitesFromDates();
            // Pour les fiches hébergement, refetch pour resynchroniser
            await ctvStore.actions.refetchStaysForSingleHebergement();
            ctvStore.actions.loadStays();
        },
        loadStays : () => {
            // Ne pas utiliser getContext() ici car cette fonction peut être appelée hors contexte d'élément
            // idProduct n'était pas utilisé de toute façon
            const { mainStay } = state;
            
            setTimeout(() => {
                if(mainStay && mainStay.secureHolidayProductAvailabilitySpecifications && mainStay.secureHolidayProductAvailabilitySpecifications.uniqueStayId){
                    const inputMainStay = document.getElementById(mainStay.secureHolidayProductAvailabilitySpecifications.uniqueStayId);
                    if(inputMainStay) {
                        inputMainStay.checked = true;
                        inputMainStay.setAttribute('checked', 'checked');
                    }
                }
            }, 100);


        },
        setMainStay: () => {
            const element = getElement();
            const context = getContext();
            if (!context || !element || !element.ref) {
                return;
            }
            const idStay = parseInt(element.ref.getAttribute('id'));
            const allStays = [...state.currentDisponibilites, ...state.currentContrepropositions];
            const mainStay = allStays.find(stay => stay.secureHolidayProductAvailabilitySpecifications.uniqueStayId === idStay);
            context.mainStay = mainStay;
        }
    }
});